import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatDate } from '@/lib/utils';
import { getAccountPipelineSummary } from '../api';

interface PipelineSummaryCardProps {
  accountId: string;
  onNavigateToTab: (tab: string) => void;
}

export default function PipelineSummaryCard({
  accountId,
  onNavigateToTab,
}: PipelineSummaryCardProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-pipeline-summary', accountId],
    queryFn: () => getAccountPipelineSummary(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pipeline Summary</CardTitle>
          <CardDescription>Active deals for this account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pipeline Summary</CardTitle>
          <CardDescription>Active deals for this account</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Unable to load pipeline data
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Pipeline Summary</CardTitle>
        <CardDescription>Active deals for this account</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pipeline Value */}
        <div>
          <div className="text-2xl font-bold text-primary">
            {formatCurrency(data.totalPipelineValue, 'CAD')}
          </div>
          <p className="text-xs text-muted-foreground">
            {data.openDeals} open deal{data.openDeals !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Stage Breakdown */}
        {data.stages.length > 0 && (
          <div className="space-y-1.5">
            {data.stages.map((stage) => (
              <StageBar
                key={stage.stage}
                label={stage.label}
                count={stage.count}
                total={data.openDeals}
              />
            ))}
          </div>
        )}

        {/* Next Closing Deal */}
        {data.nextClosing && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-1">Closing soon</p>
            <p className="text-sm font-medium truncate" title={data.nextClosing.title}>
              {data.nextClosing.title}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDate(data.nextClosing.closeDate)}
              {data.nextClosing.daysUntilClose <= 7 && (
                <span className="ml-1 text-orange-500 font-medium">
                  ({data.nextClosing.daysUntilClose}d)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Empty State */}
        {data.openDeals === 0 && (
          <p className="text-muted-foreground text-sm">
            No active deals
          </p>
        )}

        {/* View All Link */}
        <button
          onClick={() => onNavigateToTab('opportunities')}
          className="text-xs text-primary hover:underline"
        >
          View all opportunities &rarr;
        </button>
      </CardContent>
    </Card>
  );
}

interface StageBarProps {
  label: string;
  count: number;
  total: number;
}

function StageBar({ label, count, total }: StageBarProps) {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <span className="text-xs text-muted-foreground w-24 truncate" title={label}>
        {label} ({count})
      </span>
    </div>
  );
}
